import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { MessageCircle, Send, Loader2, X, ThumbsUp, ThumbsDown } from "lucide-react";
import { BRANDING } from "@/lib/branding.config";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Markdown components for styling
const MarkdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-bold mb-1 mt-2 first:mt-0">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm">{children}</li>,
  code: ({ inline, children }) => 
    inline 
      ? <code className="bg-black/10 px-1 py-0.5 text-xs font-mono rounded">{children}</code>
      : <code className="block bg-black/10 p-2 text-xs font-mono rounded my-2 overflow-x-auto">{children}</code>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800">{children}</a>,
};

export default function PublicChat() {
  const { chatbotId } = useParams();
  const [chatbot, setChatbot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [visitorId, setVisitorId] = useState(null);
  const [error, setError] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({});
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchChatbot = async () => {
      try {
        const res = await axios.get(`${API}/public/chatbot/${chatbotId}`);
        const data = res.data;
        setChatbot(data);
        if (data.lead_form_enabled && data.lead_form_fields?.length > 0) {
          setShowLeadForm(true);
        } else {
          initializeChat(data);
        }
      } catch (e) {
        console.error("Failed to load chatbot:", e.message);
        setError("Chatbot not found or not available");
      } finally {
        setLoading(false);
      }
    };
    fetchChatbot();
    setSessionId(crypto.randomUUID());
    setVisitorId(crypto.randomUUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const initializeChat = (bot, leadJustSubmitted) => {
    let welcomeMsg = bot.welcome_message || `Hello! I'm ${bot.name}. How can I help you today?`;
    if (leadJustSubmitted && bot.lead_form_success_message) {
      welcomeMsg = bot.lead_form_success_message;
    }
    setMessages([{
      id: "welcome",
      role: "assistant",
      content: welcomeMsg
    }]);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingRequired = chatbot.lead_form_fields
      .filter(f => f.required && !leadData[f.field_name])
      .map(f => f.label);
    
    if (missingRequired.length > 0) {
      alert(`Please fill in: ${missingRequired.join(", ")}`);
      return;
    }

    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        chatbot_id: chatbotId,
        visitor_data: leadData,
        session_id: sessionId
      });
      setShowLeadForm(false);
      initializeChat(chatbot, true);
    } catch (e) {
      console.error("Failed to submit lead:", e);
      // Continue anyway
      setShowLeadForm(false);
      initializeChat(chatbot);
    } finally {
      setLeadSubmitting(false);
    }
  };

  const handleFaqClick = (faq) => {
    // Add question as user message
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: "user",
      content: faq.question
    }]);
    
    // Add answer as bot message (no API call)
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now().toString() + "-answer",
        role: "assistant",
        content: faq.answer
      }]);
    }, 300);
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/chatbots/${chatbotId}/chat`, {
        message: userMessage.content,
        session_id: sessionId,
        visitor_id: visitorId
      });

      setMessages(prev => [...prev, {
        id: res.data.message_id,
        role: "assistant",
        content: res.data.response
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        id: "error-" + Date.now(),
        role: "assistant",
        content: e.response?.status === 422
          ? "Message is too long. Please keep it under 8,000 characters."
          : e.response?.status === 429
          ? "Terlalu banyak mesej dalam masa yang singkat. Sila tunggu sebentar dan cuba lagi."
          : "Sorry, I'm having trouble responding right now. Please try again."
      }]);
    } finally {
      setSending(false);
    }
  };

  const [ratedIds, setRatedIds] = useState({});
  const [feedbackMsgId, setFeedbackMsgId] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const rateMsg = async (mid, rating) => {
    if (!mid || ratedIds[mid]) return;
    setRatedIds((p) => ({ ...p, [mid]: rating }));
    if (rating === "down") {
      setFeedbackMsgId(mid);
      setFeedbackText("");
    }
    try {
      await messagesApi.rate(mid, rating, (sessionId || ""), "");
    } catch (e) {
      setRatedIds((p) => { const n = { ...p }; delete n[mid]; return n; });
    }
  };
  const submitFeedback = async () => {
    if (!feedbackMsgId) return;
    const text = (feedbackText || "").trim();
    if (!text) { setFeedbackMsgId(null); return; }
    try {
      await messagesApi.rate(feedbackMsgId, "down", (sessionId || ""), text);
    } catch (e) {}
    setFeedbackMsgId(null);
    setFeedbackText("");
  };
  const handleClose = () => {
    window.parent.postMessage("bicarra-close", "*"); window.parent.postMessage("airizbot-close", "*"); /* legacy event kept for backward compatibility with older embeds */
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-background p-8 text-center">
        <div>
          <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Theme/appearance settings
  const appearance = chatbot?.appearance || {};
  const primaryColor = appearance.primary_color || "#0A0A0A";
  const headerColor = appearance.header_color || primaryColor;
  const headerGradient = appearance.header_gradient || null;
  const accentColor = appearance.accent || null;
  const headerBackground = headerGradient || headerColor;
  const avatarOrbStyle = accentColor ? {
    background: `radial-gradient(circle at 30% 30%, ${accentColor}, ${primaryColor})`,
    boxShadow: `0 0 18px ${accentColor}66`
  } : {};
  const textColor = appearance.text_color || "#FFFFFF";
  const fontFamily = appearance.font_family || "IBM Plex Sans";
  const bubbleStyle = appearance.bubble_style || "rounded";
  const showBranding = appearance.show_branding !== false;
  const avatarUrl = chatbot?.avatar_url;
  const themeId = appearance.theme_preset || null;
  const isDark = themeId === 'midnight-aurora' || themeId === 'webby-dark' || themeId === 'pink-pulse';
  const isMidnight = themeId === 'midnight-aurora';
  const isWebby = themeId === 'webby-dark';
  const isSunny = themeId === 'sunny-yellow';
  const isEmerald = themeId === 'emerald-breeze';
  const isCoral = themeId === 'coral-sunset';
  const isLilac = themeId === 'lilac-bot';
  const isPink = themeId === 'pink-pulse';
  const bodyBgStyle = isMidnight ? {
    background: 'radial-gradient(1200px 600px at 0% 0%, #1E1B4B22 0%, transparent 60%), radial-gradient(900px 500px at 100% 100%, #312E8133 0%, transparent 60%), #0B1220',
    color: textColor
  } : isWebby ? {
    background: 'radial-gradient(800px 400px at 100% 0%, #FACC1518 0%, transparent 60%), #0A0A0A',
    color: '#FAFAFA'
  } : isSunny ? {
    background: 'linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 100%)',
    color: '#1F1300'
  } : isEmerald ? {
    background: '#F9FAFB',
    color: '#0F172A'
  } : isCoral ? {
    background: '#F9FAFB',
    color: '#0F172A'
  } : isLilac ? {
    background: 'linear-gradient(180deg, #FAF5FF 0%, #FFFFFF 100%)',
    color: '#1E1033'
  } : isPink ? {
    background: 'radial-gradient(800px 400px at 0% 0%, #EC489922 0%, transparent 60%), radial-gradient(700px 350px at 100% 100%, #BE185D33 0%, transparent 60%), #0A0A0A',
    color: '#FFFFFF'
  } : (themeId ? { background: '#FFFFFF', color: '#0F172A' } : {});
  const botBubbleStyle = isMidnight
    ? { background: 'rgba(124, 156, 255, 0.14)', color: textColor, backdropFilter: 'blur(12px)', border: '1px solid rgba(165, 180, 252, 0.18)' }
    : isWebby
    ? { background: 'rgba(255,255,255,0.06)', color: '#FAFAFA', backdropFilter: 'blur(10px)', border: '1px solid rgba(250, 204, 21, 0.20)' }
    : isSunny
    ? { background: '#FFFFFF', color: '#1F1300', border: '1px solid #FCD34D', boxShadow: '0 1px 2px rgba(250, 204, 21, 0.15)' }
    : isEmerald
    ? { background: '#FFFFFF', color: '#0F172A', border: '1px solid #6EE7B7', borderRadius: 18 }
    : isCoral
    ? { background: '#FFFFFF', color: '#0F172A', border: '1px solid #FCA5A5' }
    : isLilac
    ? { background: '#F5F0FF', color: '#1E1033', border: '1px solid #DDD6FE', borderRadius: 18 }
    : isPink
    ? { background: 'rgba(236, 72, 153, 0.10)', color: '#FFFFFF', backdropFilter: 'blur(12px)', border: '1px solid rgba(244, 114, 182, 0.25)' }
    : (themeId ? { background: '#F1F5FF', color: '#0F172A', border: '1px solid #E2E8F8' } : {});
  const botAvatarFallbackStyle = accentColor
    ? { background: `radial-gradient(circle at 30% 30%, ${accentColor}, ${primaryColor})`, boxShadow: `0 0 10px ${accentColor}55` }
    : {};
  const inputWrapStyle = isMidnight
    ? { background: 'rgba(15, 23, 42, 0.6)', borderTop: '1px solid rgba(165, 180, 252, 0.18)', backdropFilter: 'blur(8px)' }
    : isWebby
    ? { background: 'rgba(10, 10, 10, 0.85)', borderTop: '1px solid rgba(250, 204, 21, 0.18)', backdropFilter: 'blur(8px)' }
    : isSunny
    ? { background: '#FFFBEB', borderTop: '1px solid #FCD34D' }
    : isEmerald
    ? { background: '#FFFFFF', borderTop: '1px solid #D1FAE5' }
    : isCoral
    ? { background: '#FFFFFF', borderTop: '1px solid #FCA5A5' }
    : isLilac
    ? { background: '#FAF5FF', borderTop: '1px solid #DDD6FE' }
    : isPink
    ? { background: 'rgba(10, 10, 10, 0.85)', borderTop: '1px solid rgba(236, 72, 153, 0.25)', backdropFilter: 'blur(8px)' }
    : (themeId ? { background: '#FFFFFF', borderTop: '1px solid #E2E8F8' } : {});
  const inputFieldStyle = isMidnight
    ? { background: 'rgba(30, 27, 75, 0.6)', color: textColor, border: '1px solid rgba(165, 180, 252, 0.25)', borderRadius: 999, paddingLeft: 16, paddingRight: 16 }
    : isWebby
    ? { background: 'rgba(255,255,255,0.05)', color: '#FAFAFA', border: '1px solid rgba(250, 204, 21, 0.30)', borderRadius: 999, paddingLeft: 16, paddingRight: 16 }
    : isSunny
    ? { background: '#FFFFFF', color: '#1F1300', border: '1px solid #FCD34D', borderRadius: 999, paddingLeft: 16, paddingRight: 16 }
    : isEmerald
    ? { background: '#F9FAFB', color: '#0F172A', border: '1px solid #D1FAE5', borderRadius: 999, paddingLeft: 16, paddingRight: 16 }
    : isCoral
    ? { background: '#F9FAFB', color: '#0F172A', border: '1px solid #FCA5A5', borderRadius: 999, paddingLeft: 16, paddingRight: 16 }
    : isLilac
    ? { background: '#FFFFFF', color: '#1E1033', border: '1px solid #DDD6FE', borderRadius: 999, paddingLeft: 16, paddingRight: 16 }
    : isPink
    ? { background: 'rgba(255,255,255,0.06)', color: '#FFFFFF', border: '1px solid rgba(244, 114, 182, 0.30)', borderRadius: 999, paddingLeft: 16, paddingRight: 16 }
    : (themeId ? { background: '#F8FAFF', color: '#0F172A', border: '1px solid #E2E8F8', borderRadius: 999, paddingLeft: 16, paddingRight: 16 } : {});
  const sendBtnStyle = headerGradient
    ? { background: headerGradient, color: textColor, borderRadius: 999, boxShadow: accentColor ? `0 0 14px ${accentColor}66` : 'none' }
    : { backgroundColor: primaryColor };
  const faqs = chatbot?.faqs || [];

  const getBubbleClass = () => {
    switch (bubbleStyle) {
      case "square": return "";
      case "pill": return "rounded-full px-4";
      default: return "rounded-lg";
    }
  };

  // Lead Form View
  if (showLeadForm) {
    return (
      <div className="h-screen flex flex-col bg-background" style={{ fontFamily, ...bodyBgStyle }} data-testid="lead-form">
        {/* Header */}
        <div className="p-4 flex items-center justify-between" style={{ background: headerBackground, color: textColor }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 flex items-center justify-center overflow-hidden rounded-full" style={avatarOrbStyle}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <MessageCircle className="w-5 h-5" />
              )}
            </div>
            <div>
              <p className="font-semibold">{chatbot?.name}</p>
              <p className="text-xs opacity-80">Online</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="hover:bg-white/20" style={{ color: textColor }} onClick={handleClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Lead Form */}
        <div className="flex-1 p-6 overflow-auto">
          <h2 className="text-lg font-semibold mb-4">{chatbot?.lead_form_title || "Before we start..."}</h2>
          <form onSubmit={handleLeadSubmit} className="space-y-4">
            {chatbot?.lead_form_fields?.map((field, i) => (
              <div key={field.field_name || `field-${i}`} className="space-y-1">
                <Label className="text-sm">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {field.field_type === "textarea" ? (
                  <textarea
                    value={leadData[field.field_name] || ""}
                    onChange={(e) => setLeadData({ ...leadData, [field.field_name]: e.target.value })}
                    required={field.required}
                    placeholder={field.placeholder || ""}
                    className="w-full border border-border p-2 text-sm min-h-[80px]"
                    data-testid={`lead-field-${field.field_name}`}
                  />
                ) : field.field_type === "select" ? (
                  <select
                    value={leadData[field.field_name] || ""}
                    onChange={(e) => setLeadData({ ...leadData, [field.field_name]: e.target.value })}
                    required={field.required}
                    className="w-full border border-border p-2 text-sm bg-background"
                    data-testid={`lead-field-${field.field_name}`}
                  >
                    <option value="">{field.placeholder || "Select..."}</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    type={field.field_type}
                    value={leadData[field.field_name] || ""}
                    onChange={(e) => setLeadData({ ...leadData, [field.field_name]: e.target.value })}
                    required={field.required}
                    placeholder={field.placeholder || ""}
                    className="rounded-none"
                    data-testid={`lead-field-${field.field_name}`}
                  />
                )}
              </div>
            ))}
            <Button
              type="submit"
              className="w-full"
              disabled={leadSubmitting}
              style={{ backgroundColor: primaryColor }}
              data-testid="lead-submit-btn"
            >
              {leadSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (chatbot?.lead_form_submit_text || "Start Chat")}
            </Button>
          </form>
        </div>

        {showBranding && (
          <p className="text-xs text-muted-foreground text-center py-2 border-t border-border">
            Powered by <span className="font-semibold">{BRANDING.product.name}</span> <span className="text-[var(--gold)]" style={{letterSpacing:"0.18em",fontSize:"0.7em"}}>{BRANDING.endorser.tagline.toUpperCase()}</span>
          </p>
        )}
      </div>
    );
  }

  // Chat View
  return (
    <div className="h-screen flex flex-col bg-background" style={{ fontFamily, ...bodyBgStyle }} data-testid="public-chat">
      {/* Header */}
      <div className="p-4 flex items-center justify-between" style={{ background: headerBackground, color: textColor }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 flex items-center justify-center overflow-hidden rounded-full" style={avatarOrbStyle}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <MessageCircle className="w-5 h-5" />
            )}
          </div>
          <div>
            <p className="font-semibold">{chatbot?.name}</p>
            <p className="text-xs opacity-80">Online</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="hover:bg-white/20" style={{ color: textColor }} onClick={handleClose} data-testid="close-chat-btn">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msg.role !== "user" && (
                <div className="w-6 h-6 mr-2 flex-shrink-0 overflow-hidden rounded-full">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={botAvatarFallbackStyle}>
                      <MessageCircle className="w-3 h-3" />
                    </div>
                  )}
                </div>
              )}
              <div
                className={`max-w-[80%] p-3 ${getBubbleClass()} ${
                  msg.role === "user" ? "" : "bg-muted"
                }`}
                style={msg.role === "user" ? { backgroundColor: primaryColor, color: textColor } : botBubbleStyle}
              >
                {msg.role === "user" ? (
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                ) : (
                  <div className="text-sm prose-sm">
                    <ReactMarkdown components={MarkdownComponents}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                )}
              {msg.role !== "user" && msg.id && (
                <div className="flex items-center gap-1 mt-1 opacity-70" data-testid={`rate-row-${msg.id}`}>
                  <button type="button" onClick={() => rateMsg(msg.id, "up")} className={`p-1 rounded hover:bg-muted ${ratedIds[msg.id]==="up" ? "text-green-600" : ""}`} aria-label="Helpful" data-testid={`rate-up-${msg.id}`}>
                    <ThumbsUp className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => rateMsg(msg.id, "down")} className={`p-1 rounded hover:bg-muted ${ratedIds[msg.id]==="down" ? "text-rose-600" : ""}`} aria-label="Not helpful" data-testid={`rate-down-${msg.id}`}>
                    <ThumbsDown className="w-3 h-3" />
                  </button>
                </div>
              )}
              </div>
            </div>
          ))}
          
          {/* FAQ Buttons - show after welcome message */}
          {messages.length === 1 && faqs.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="faq-buttons">
              {faqs.map((faq, i) => (
                <button
                  key={faq.question || `faq-${i}`}
                  onClick={() => handleFaqClick(faq)}
                  className="text-xs px-3 py-2 border hover:bg-muted transition-colors text-left"
                  style={{ borderColor: primaryColor, color: primaryColor }}
                  data-testid={`faq-btn-${i}`}
                >
                  {faq.question}
                </button>
              ))}
            </div>
          )}

          {sending && (
            <div className="flex justify-start">
              <div className="w-6 h-6 mr-2 flex-shrink-0 overflow-hidden rounded-full">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={botAvatarFallbackStyle}>
                    <MessageCircle className="w-3 h-3" />
                  </div>
                )}
              </div>
              <div className={`p-3 ${getBubbleClass()}`} style={botBubbleStyle}>
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-border" style={inputWrapStyle}>
        {input.length > 7500 && (
          <p className="text-xs text-muted-foreground mb-1 text-right" data-testid="chat-input-count">
            {input.length.toLocaleString()} / 8,000
          </p>
        )}
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={8000}
            placeholder="Type a message..."
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
            disabled={sending}
            className="rounded-none"
            style={inputFieldStyle}
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={sending || !input.trim()}
            style={sendBtnStyle}
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {showBranding && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            Powered by <span className="font-semibold">{BRANDING.product.name}</span> <span className="text-[var(--gold)]" style={{letterSpacing:"0.18em",fontSize:"0.7em"}}>{BRANDING.endorser.tagline.toUpperCase()}</span>
          </p>
        )}
      </div>
    </div>
  );
}
